// Cards load lazily, so until its data arrives a card is an empty box - and when it fills in,
// everything below it moves. Picking an indicator from the sidebar makes that visible: the
// scroll target is computed on the current layout, the cards passed on the way start loading
// and grow, and the page lands short of the indicator.
//
// So every card that has actually drawn a chart reports its height, and the average is exposed
// as the CSS variable --chart-card-height, which cards still waiting for data use as their
// minimum height. A CSS variable rather than React state: updating it re-renders nothing.
//
// Heights depend on the viewport - the charts are sized in vh and the metadata panel moves
// below the chart on narrow screens - so the average is kept per viewport size, and persisted,
// so that a fresh page load (or a shared link pointing at an indicator further down) starts
// from the right value before a single chart has rendered.

export const CARD_HEIGHT_VARIABLE = "--chart-card-height";

const STORAGE_KEY = "afs.chartCardHeight";
// Ignore changes smaller than this: sub-pixel resizes would otherwise rewrite the variable
// constantly while the charts settle.
const MIN_CHANGE_PX = 4;

// Width buckets follow the MUI breakpoints the layout switches on; height is rounded because
// every vh-sized chart scales with it.
export const viewportKey = (width, height) => {
    const band = width >= 1200 ? "lg" : width >= 900 ? "md" : width >= 600 ? "sm" : "xs";
    return `${band}-${Math.round(height / 50) * 50}`;
};

export const averageHeight = (heights) => {
    const values = [...heights].filter((height) => Number.isFinite(height) && height > 0);
    if (values.length === 0) return null;
    return Math.round(values.reduce((sum, height) => sum + height, 0) / values.length);
};

const readStored = () => {
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

const writeStored = (stored) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // Private mode or storage disabled: the average still works for this session.
    }
};

// One height per card, keyed by its id: a card that resizes several times must not weigh more
// than one that rendered once.
let heights = new Map();
let currentKey = null;
let applied = null;

const currentViewportKey = () => viewportKey(window.innerWidth, window.innerHeight);

const apply = (height) => {
    const root = document.documentElement;
    if (height == null) {
        root.style.removeProperty(CARD_HEIGHT_VARIABLE);
    } else {
        root.style.setProperty(CARD_HEIGHT_VARIABLE, `${height}px`);
    }
    applied = height;
};

// Called whenever the viewport may have changed size: the samples collected so far describe a
// layout that no longer exists, so start again from what was stored for the new size.
const syncViewport = () => {
    const key = currentViewportKey();
    if (key === currentKey) return;
    currentKey = key;
    heights = new Map();
    apply(readStored()[key] ?? null);
};

export const reportCardHeight = (cardId, height) => {
    if (typeof window === "undefined" || !cardId) return;
    syncViewport();
    heights.set(cardId, height);

    const average = averageHeight(heights.values());
    if (average == null || (applied != null && Math.abs(average - applied) < MIN_CHANGE_PX)) return;

    apply(average);
    writeStored({ ...readStored(), [currentKey]: average });
};

export const forgetCardHeight = (cardId) => {
    heights.delete(cardId);
};

if (typeof window !== "undefined") {
    syncViewport();
    window.addEventListener("resize", syncViewport);
}
